import { decodePolyline } from "@/lib/utils"
import { haversineKm, projectOnPathKm } from "@/lib/route-matching"
import { parseDurationToMinutes } from "@/lib/segment-times"

export const ROUTE_SAMPLE_TARGET = 60

export interface RoutePoint {
  lat: number
  lng: number
}

export interface LocationLike {
  lat?: number
  lng?: number
  location?: { coordinates?: number[] }
}

export interface StopLike extends LocationLike {
  name?: string
  placeId?: string
  price?: number
  order?: number
}

export interface DenormalizeInput {
  origin?: LocationLike | null
  destination?: LocationLike | null
  stops?: StopLike[]
  route?: {
    polyline?: string
    sampledCoordinates?: RoutePoint[]
    duration?: string
    distance?: string
  }
}

export interface DenormalizeResult {
  route: {
    coordinates: { type: "LineString"; coordinates: Array<[number, number]> }
    totalKm: number
    durationMin: number
  }
  stops: Array<StopLike & { alongKm: number }>
}

function locationPoint(loc: LocationLike | null | undefined): RoutePoint | null {
  if (loc?.location?.coordinates && loc.location.coordinates.length >= 2) {
    return { lat: loc.location.coordinates[1], lng: loc.location.coordinates[0] }
  }
  if (typeof loc?.lat === "number" && typeof loc?.lng === "number") {
    return { lat: loc.lat, lng: loc.lng }
  }
  return null
}

export function samplePath(points: RoutePoint[], targetCount: number): RoutePoint[] {
  if (points.length <= targetCount || targetCount < 2) return points
  const totalKm = pathKm(points)
  if (totalKm <= 0) return [points[0], points[points.length - 1]]
  const stepKm = totalKm / (targetCount - 1)
  const sampled: RoutePoint[] = [points[0]]
  let accumulated = 0
  let segmentIndex = 0
  for (let s = 1; s < targetCount - 1; s++) {
    const target = s * stepKm
    while (
      segmentIndex < points.length - 1 &&
      accumulated +
        haversineKm(
          points[segmentIndex].lat,
          points[segmentIndex].lng,
          points[segmentIndex + 1].lat,
          points[segmentIndex + 1].lng
        ) <
        target
    ) {
      accumulated += haversineKm(
        points[segmentIndex].lat,
        points[segmentIndex].lng,
        points[segmentIndex + 1].lat,
        points[segmentIndex + 1].lng
      )
      segmentIndex++
    }
    const a = points[segmentIndex]
    const b = points[segmentIndex + 1]
    const segmentKm = haversineKm(a.lat, a.lng, b.lat, b.lng)
    const frac = segmentKm > 0 ? (target - accumulated) / segmentKm : 0
    sampled.push({
      lat: a.lat + (b.lat - a.lat) * frac,
      lng: a.lng + (b.lng - a.lng) * frac,
    })
  }
  sampled.push(points[points.length - 1])
  return sampled
}

function pathKm(path: RoutePoint[]): number {
  let total = 0
  for (let i = 0; i < path.length - 1; i++) {
    total += haversineKm(path[i].lat, path[i].lng, path[i + 1].lat, path[i + 1].lng)
  }
  return total
}

export function denormalizeRoute(input: DenormalizeInput): DenormalizeResult | null {
  const originPt = locationPoint(input.origin)
  const destPt = locationPoint(input.destination)

  let fullPath: RoutePoint[] = []
  if (input.route?.polyline) {
    try {
      fullPath = decodePolyline(input.route.polyline)
    } catch {
      fullPath = []
    }
  }
  if (
    fullPath.length < 2 &&
    input.route?.sampledCoordinates &&
    input.route.sampledCoordinates.length >= 2
  ) {
    fullPath = input.route.sampledCoordinates.map((p) => ({ lat: p.lat, lng: p.lng }))
  }
  if (fullPath.length < 2 && originPt && destPt) {
    fullPath = [originPt, destPt]
  }
  if (fullPath.length < 2) return null

  if (originPt) fullPath[0] = originPt
  if (destPt) fullPath[fullPath.length - 1] = destPt

  const totalKm = Math.round(pathKm(fullPath) * 100) / 100
  const durationMin = parseDurationToMinutes(input.route?.duration || "")

  const stops: Array<StopLike & { alongKm: number }> = (input.stops || []).map((s) => {
    const p = locationPoint(s)
    let alongKm = 0
    if (p) {
      const proj = projectOnPathKm(p, fullPath)
      alongKm = proj ? Math.round(proj.alongKm * 100) / 100 : 0
    }
    return { ...s, alongKm }
  })

  const sampledPath = samplePath(fullPath, ROUTE_SAMPLE_TARGET)
  if (originPt) sampledPath[0] = originPt
  if (destPt) sampledPath[sampledPath.length - 1] = destPt
  const coordinates: Array<[number, number]> = sampledPath.map((p) => [p.lng, p.lat])

  return {
    route: {
      coordinates: { type: "LineString", coordinates },
      totalKm,
      durationMin,
    },
    stops,
  }
}
